/**
 * «Карточка моей идеи» — стержневой персональный контекст курса.
 *
 * Два режима:
 *   - variant="full"    — большая карточка на home: либо мастер заполнения
 *                         (если idea_card пуст), либо отображение + кнопка «правка».
 *   - variant="compact" — компактная плашка в шапке страницы модуля.
 *                         Только summary + кнопка «открыть/править».
 *
 * Данные читаются из useMeQuery (TanStack Query кэширует), пишутся через
 * PUT /v1/cabinet/me/idea-card. После save — invalidate ['me'].
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { api } from '../lib/api';
import type { IdeaCard, IdeaCardMilestone, MeResponse } from '../types/cabinet';

const EMPTY: IdeaCard = {
  niche: '',
  audience: '',
  pain: '',
  target_revenue: null,
  deadline_days: null,
};

function isFilled(c: IdeaCard | null | undefined): boolean {
  if (!c) return false;
  return Boolean(c.niche || c.audience || c.pain || c.target_revenue || c.deadline_days);
}

interface Props {
  variant: 'full' | 'compact';
  /** Поверхностный me от родителя — чтобы не делать дублирующий запрос. */
  me?: MeResponse;
}

export function IdeaCardBlock({ variant, me }: Props) {
  // Если родитель не передал me — берём из кэша.
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: async (): Promise<MeResponse> => (await api.get<MeResponse>('/v1/cabinet/me')).data,
    enabled: !me,
  });
  const meData = me ?? meQuery.data;
  if (!meData) return null;

  if (variant === 'compact') {
    return <CompactCard ideaCard={meData.idea_card} />;
  }
  return <FullCard ideaCard={meData.idea_card} />;
}

// ---------------------------------------------------------------------------
// Compact — в шапке страницы модуля
// ---------------------------------------------------------------------------

function CompactCard({ ideaCard }: { ideaCard: IdeaCard | null }) {
  const [open, setOpen] = useState(false);
  if (!isFilled(ideaCard)) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mb-6 block w-full rounded-xl border-2 border-dashed border-brand-300 dark:border-brand-500/40 bg-brand-50/30 dark:bg-brand-500/5 px-4 py-3 text-left text-sm text-brand-800 dark:text-brand-200 transition hover:bg-brand-50 dark:hover:bg-brand-500/10"
      >
        <span className="font-medium">Заполни «Карточку моей идеи»</span> — стержневой контекст
        курса. Все модули будут адаптированы под твою идею.{' '}
        <span className="underline">Открыть</span>
        {open && <EditModal initial={ideaCard ?? EMPTY} onClose={() => setOpen(false)} />}
      </button>
    );
  }
  return (
    <div className="mb-6 rounded-xl bg-gradient-to-br from-brand-50 to-white dark:from-brand-500/10 dark:to-slate-900 px-4 py-3 ring-1 ring-brand-200 dark:ring-brand-500/30">
      <div className="mb-1 flex items-center justify-between gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-700 dark:text-brand-300">
          Моя идея
        </p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs font-medium text-brand-700 dark:text-brand-300 hover:underline"
        >
          править
        </button>
      </div>
      <div className="space-y-1 text-sm text-slate-800 dark:text-slate-100">
        {ideaCard?.niche && (
          <div>
            <span className="text-slate-500 dark:text-slate-400">Ниша: </span>
            {ideaCard.niche}
          </div>
        )}
        {ideaCard?.audience && (
          <div>
            <span className="text-slate-500 dark:text-slate-400">Кому: </span>
            {ideaCard.audience}
          </div>
        )}
        {ideaCard?.pain && (
          <div>
            <span className="text-slate-500 dark:text-slate-400">Боль: </span>
            {ideaCard.pain}
          </div>
        )}
        {(ideaCard?.target_revenue || ideaCard?.deadline_days) && (
          <div>
            <span className="text-slate-500 dark:text-slate-400">Цель: </span>
            {ideaCard.target_revenue
              ? `${ideaCard.target_revenue.toLocaleString('ru-RU')} ₽/мес`
              : ''}
            {ideaCard.target_revenue && ideaCard.deadline_days ? ' за ' : ''}
            {ideaCard.deadline_days ? `${ideaCard.deadline_days} дн.` : ''}
          </div>
        )}
      </div>
      {open && <EditModal initial={ideaCard ?? EMPTY} onClose={() => setOpen(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Full — карточка на главной home
// ---------------------------------------------------------------------------

function FullCard({ ideaCard }: { ideaCard: IdeaCard | null }) {
  const [editing, setEditing] = useState(!isFilled(ideaCard));
  // Если ideaCard приходит позже (из refetch) и был пуст — открываем мастер.
  useEffect(() => {
    if (!isFilled(ideaCard)) setEditing(true);
  }, [ideaCard]);

  if (editing) {
    return (
      <section className="mb-6 rounded-2xl bg-gradient-to-br from-brand-50 to-white dark:from-brand-500/10 dark:to-slate-900 p-5 ring-2 ring-brand-200 dark:ring-brand-500/30 sm:p-6">
        <header className="mb-1 flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-brand-700 dark:text-brand-300">
            Карточка моей идеи
          </span>
        </header>
        <h2 className="mb-1 text-xl font-bold text-slate-900 dark:text-slate-100">
          {isFilled(ideaCard) ? 'Правка карточки' : 'Опиши идею за 2 минуты'}
        </h2>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">
          Эта карточка — стержневой персональный контекст. Она будет в шапке каждого модуля. Не надо
          красиво — пиши как есть, потом можно править.
        </p>
        <EditForm
          initial={ideaCard ?? EMPTY}
          onSave={() => setEditing(false)}
          onCancel={isFilled(ideaCard) ? () => setEditing(false) : undefined}
        />
      </section>
    );
  }

  // Заполнено — отображаем красиво.
  return (
    <section className="mb-6 rounded-2xl bg-gradient-to-br from-brand-50 to-white dark:from-brand-500/10 dark:to-slate-900 p-5 ring-1 ring-brand-200 dark:ring-brand-500/30 sm:p-6">
      <header className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-700 dark:text-brand-300">
            Моя идея
          </p>
          <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100 sm:text-2xl">
            {ideaCard?.niche || 'Без названия'}
          </h2>
        </div>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="shrink-0 rounded-lg bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-brand-700 dark:text-brand-300 ring-1 ring-brand-200 dark:ring-brand-500/30 transition hover:bg-brand-50 dark:hover:bg-brand-500/10"
        >
          править
        </button>
      </header>
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {ideaCard?.audience && (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Кому это полезно
            </dt>
            <dd className="mt-0.5 text-sm text-slate-800 dark:text-slate-100">
              {ideaCard.audience}
            </dd>
          </div>
        )}
        {ideaCard?.pain && (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Какую боль решает
            </dt>
            <dd className="mt-0.5 text-sm text-slate-800 dark:text-slate-100">{ideaCard.pain}</dd>
          </div>
        )}
        {ideaCard?.target_revenue && (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Цель — выручка в месяц
            </dt>
            <dd className="mt-0.5 text-sm font-semibold text-slate-900 dark:text-slate-100">
              {ideaCard.target_revenue.toLocaleString('ru-RU')} ₽
            </dd>
          </div>
        )}
        {ideaCard?.deadline_days && (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              За сколько дней
            </dt>
            <dd className="mt-0.5 text-sm font-semibold text-slate-900 dark:text-slate-100">
              {ideaCard.deadline_days} дней
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit form / modal
// ---------------------------------------------------------------------------

export function IdeaCardEditModal({
  initial,
  onClose,
  milestone,
  title,
  intro,
}: {
  initial: IdeaCard;
  onClose: () => void;
  milestone?: IdeaCardMilestone;
  title?: string;
  intro?: string;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/60 px-4 py-10 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-xl rounded-2xl bg-white dark:bg-slate-900 p-5 shadow-xl sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
              {title ?? 'Карточка моей идеи'}
            </h2>
            {intro && <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{intro}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 transition hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100"
            aria-label="Закрыть"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
              <path
                fillRule="evenodd"
                d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 011.4 1.4L11.4 10l4.3 4.3a1 1 0 01-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 01-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </header>
        <EditForm initial={initial} onSave={onClose} onCancel={onClose} milestone={milestone} />
      </div>
    </div>
  );
}

function EditModal({ initial, onClose }: { initial: IdeaCard; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/60 px-4 py-10 backdrop-blur-sm"
      onClick={(e) => {
        // Закрыть по клику на backdrop.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-xl rounded-2xl bg-white dark:bg-slate-900 p-5 shadow-xl sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            Карточка моей идеи
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 transition hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100"
            aria-label="Закрыть"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
              <path
                fillRule="evenodd"
                d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 011.4 1.4L11.4 10l4.3 4.3a1 1 0 01-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 01-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </header>
        <EditForm initial={initial} onSave={onClose} onCancel={onClose} />
      </div>
    </div>
  );
}

function EditForm({
  initial,
  onSave,
  onCancel,
  milestone,
}: {
  initial: IdeaCard;
  onSave: () => void;
  onCancel?: () => void;
  /** Если правка идёт из «обнови после Модуля X» — фронт пометит снимок. */
  milestone?: IdeaCardMilestone;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<IdeaCard>(initial);

  const mutation = useMutation({
    mutationFn: async (body: IdeaCard): Promise<IdeaCard> => {
      const url = milestone
        ? `/v1/cabinet/me/idea-card?milestone=${milestone}`
        : '/v1/cabinet/me/idea-card';
      const resp = await api.put<IdeaCard>(url, body);
      return resp.data;
    },
    onSuccess: (saved) => {
      // Освежаем кеш me — карточка должна обновиться везде сразу.
      const prev = qc.getQueryData<MeResponse>(['me']);
      if (prev) {
        qc.setQueryData<MeResponse>(['me'], { ...prev, idea_card: saved });
      }
      // Инвалидируем историю — там появится новый снимок.
      qc.invalidateQueries({ queryKey: ['idea-card-history'] });
      onSave();
    },
  });

  const update = <K extends keyof IdeaCard>(key: K, value: IdeaCard[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate(form);
      }}
      className="space-y-4"
    >
      <Field label="Ниша / о чём идея" placeholder="например: помощь мамам наладить сон ребёнка">
        <input
          type="text"
          value={form.niche}
          maxLength={200}
          onChange={(e) => update('niche', e.target.value)}
          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
        />
      </Field>
      <Field label="Кому это полезно" placeholder="например: мамы детей 0-3 лет в декрете">
        <input
          type="text"
          value={form.audience}
          maxLength={200}
          onChange={(e) => update('audience', e.target.value)}
          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
        />
      </Field>
      <Field
        label="Какую боль решает"
        placeholder="например: ребёнок не спит дольше 40 минут, мама в выгорании"
      >
        <textarea
          value={form.pain}
          maxLength={300}
          rows={2}
          onChange={(e) => update('pain', e.target.value)}
          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Цель — ₽/мес" placeholder="80000">
          <input
            type="number"
            min={0}
            step={1000}
            value={form.target_revenue ?? ''}
            onChange={(e) =>
              update('target_revenue', e.target.value === '' ? null : Number(e.target.value))
            }
            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
          />
        </Field>
        <Field label="За сколько дней" placeholder="60">
          <input
            type="number"
            min={1}
            max={365}
            value={form.deadline_days ?? ''}
            onChange={(e) =>
              update('deadline_days', e.target.value === '' ? null : Number(e.target.value))
            }
            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
          />
        </Field>
      </div>
      {mutation.error && (
        <p className="text-sm text-rose-700 dark:text-rose-400">
          Не удалось сохранить. Попробуй ещё раз.
        </p>
      )}
      <div className="flex gap-2 pt-1 sm:justify-end">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-xl bg-slate-100 dark:bg-slate-800 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 transition hover:bg-slate-200 dark:hover:bg-slate-700 sm:flex-initial"
          >
            Отмена
          </button>
        )}
        <button
          type="submit"
          disabled={mutation.isPending}
          className="flex-1 rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60 sm:flex-initial"
        >
          {mutation.isPending ? 'Сохраняю…' : 'Сохранить'}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  placeholder: _placeholder,
  children,
}: {
  label: string;
  placeholder?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-600 dark:text-slate-300">
        {label}
      </span>
      {children}
    </label>
  );
}
